import { Head } from "@inertiajs/react";
import CtaBand from "@/components/marketing/cta-band";
import PageHero from "@/components/marketing/page-hero";
import SectionHeading from "@/components/marketing/section-heading";
import ServiceCoverageMatrix from "@/components/marketing/service-coverage-matrix";
import SmartLink from "@/components/marketing/smart-link";
import enterpriseServiceNowIllustration from "@/images/marketing/enterprise-servicenow-obsero.png";
import serviceNowLogo from "@/images/marketing/servicenow.svg";
import PublicLayout from "@/layouts/public-layout";
import { siteLinks } from "@/data/marketing-site";

const enterpriseHighlights = [
  {
    title: "Første modul i drift på 12 uger",
    copy: "Med Obsero Enterprise følger I en struktureret proces pr. modul: 2 uger kortlægning, 8 uger implementering, og så er modulet i drift. Ikke næsten. Ikke \"vi er ved at være der\". I drift."
  },
  {
    title: "En standard, der fjerner gætteriet",
    copy: "Det kræver dyb lovgivningsmæssig indsigt, teknisk ekspertise og hundredvis af timers arbejde at bygge sit compliance-setup fra bunden. Obsero Enterprise er baseret på en gennemtestet standard, der allerede dækker alle krav i NIS2 eller DORA. I går direkte ind i et færdigt system — og bruger jeres tid på at drive forretning, ikke på at opfinde løsninger."
  },
  {
    title: "Vi tager ansvaret for jeres compliance",
    copy: "Lovgivning ændrer sig. Trusselsbilledet ændrer sig. Jeres forretning ændrer sig. Obsero er jeres faste compliance-partner. Med løbende health checks, opdateringer og faglig rådgivning sørger vi for, at jeres system altid afspejler virkeligheden. I skal ikke holde styr på det. Det gør vi."
  }
];

const deliveryPhases = [
  {
    phase: "Scope",
    icon: "bi bi-bullseye",
    copy: "Behovsanalyse, fit-vurdering, forberedelse og en klar køreplan for den videre implementering.",
    deliverables: ["Behovsafklaring", "Fit-vurdering", "Implementeringsplan"]
  },
  {
    phase: "Etablering",
    icon: "bi bi-gear-wide-connected",
    copy: "Fire moduler implementeres, ServiceNow konfigureres, automatisering bygges ind, og organisationen trænes og overtager.",
    deliverables: ["4 moduler i ServiceNow", "Automatisering", "Onboarding og overdragelse"]
  },
  {
    phase: "Compliance i drift",
    icon: "bi bi-check2-circle",
    copy: "Løbende opdateringer, faglig rådgivning, health checks og en fast partnerskabsmodel, der holder løsningen levende.",
    deliverables: ["Health checks", "Løbende opdateringer", "Fast compliance-partner"]
  }
];

const partnershipCards = [
  {
    title: "Fast månedlig ydelse fra dag ét.",
    copy: "Fuld budgetkontrol, ingen uforudsete engangsinvesteringer."
  },
  {
    title: "Alt inkluderet:",
    copy: "Procesmotor, specialister og løbende vedligeholdelse samlet i én leverancemodel. I betaler én pris — vi leverer det hele."
  },
  {
    title: "3-årigt partnerskab",
    copy: "Vi forpligter os til, at jeres løsning virker — også når lovgivningen ændrer sig."
  }
];

const enterprisePriorityReasons = [
  "Compliance bliver kun sværere at få styr på, jo længere I venter. Kravene vokser, dokumentationen samler sig i flere systemer, og ansvaret bliver sværere at placere.",
  "Med Obsero Enterprise køber I ikke endnu et projekt, men adgang til et færdigt setup, der omsætter krav til processer, opgaver og dokumentation i drift.",
  "I får hurtigere fremdrift, lavere intern belastning og et klart grundlag for at vise ledelsen, revisorer og myndigheder, at compliance ikke bare er planlagt — men fungerer."
];

const serviceNowModuleSupport = [
  {
    title: "Governance & Ledelsesansvar",
    icon: "bi bi-diagram-3",
    copy: "Modulet samler alle politikker, retningslinjer og strategiske mål ét sted. Det udsender automatisk opgaver til ledelsen, som skal godkende risikoniveauer og sikkerhedsstrategier.",
    serviceNow: false
  },
  {
    title: "Hændelseshåndtering ITSM",
    icon: "bi bi-bell",
    copy: "Vi samler hændelseshåndtering, driftskontinuitet, krisestyring, hændelsesrapportering samt efterbehandling og forbedringer i én sammenhængende og værktøjsunderstøttet ramme.",
    serviceNow: true
  },
  {
    title: "Risikohåndtering",
    icon: "bi bi-shield-check",
    copy: "Vi leverer en risikostyringsløsning, som understøtter kravene i NIS2-reguleringen og hjælper jer med at implementere risikostyring af net- og informationssystemer i overensstemmelse med NIS2-standarden.",
    serviceNow: true
  },
  {
    title: "Leverandørstyring",
    icon: "bi bi-people",
    copy: "Vi samler 3. parts risikovurdering, kontraktstyring og leverandøropfølgning i én sammenhængende og værktøjsunderstøttet ramme — driftssat direkte i ServiceNow.",
    serviceNow: true
  }
];

const enterpriseServiceColumns = [
  { key: "governance", label: "Governance & ledelsesansvar" },
  { key: "incident", label: "Hændelses\u00ADhåndtering / ITSM" },
  { key: "risk", label: "Risikostyring" },
  { key: "vendor", label: "Leverandørstyring" }
];

const enterpriseServiceRows = [
  {
    label: "Strategier og governance-rammer",
    coverage: ["governance"]
  },
  {
    label: "Regulatoriske og interne politikker",
    coverage: ["governance"]
  },
  {
    label: "Ledelsesrapportering og beslutningsspor",
    coverage: ["governance"]
  },
  {
    label: "Compliance-dokumentation og evidens",
    coverage: ["governance", "incident", "risk", "vendor"]
  },
  {
    label: "Practice- og procesdokumentation",
    coverage: ["incident", "risk", "vendor"]
  },
  {
    label: "Vejledninger og uddannelse",
    coverage: ["incident", "risk", "vendor"]
  },
  {
    label: "Implementeret værktøjsunderstøttelse",
    coverage: ["incident", "risk", "vendor"]
  },
  {
    label: "Versionsopdatering af ServiceNow",
    coverage: ["incident", "risk", "vendor"]
  }
];

function Enterprise() {
  return (
    <>
      <Head title="Obsero Enterprise">
        <meta
          head-key="description"
          name="description"
          content="Danmarks eneste fuldt ServiceNow-understøttede NIS2 eller DORA compliance-løsning."
        />
      </Head>

      <PageHero
        narrow
        size="compact"
        title="Danmarks eneste fuldt ServiceNow-understøttede NIS2 eller DORA compliance-løsning."
        copy="De fleste betaler for en plan. I køber et system. Obsero omsætter NIS2 og DORA til compliance i drift direkte i jeres ServiceNow — så I kan dokumentere, automatisere og bevise jeres compliance i realtid."
        actions={[
          { href: siteLinks.contact, label: "Kontakt os", className: "btn btn-light" }
        ]}
      />

      <section className="public-section public-section--white-surface">
        <div className="public-container">
          <div className="public-split-callout">
            <div className="public-split-callout__lead public-split-callout__lead--accented">
              <SectionHeading
                title="Vi installerer ikke bare et system, men en motor."
              />

              <div className="public-copy-stack">
                <p className="public-section__copy">
                  Obsero Enterprise er bygget på en gennemtestet standard — udviklet specifikt til NIS2 eller DORA og forankret direkte i ServiceNow. I stedet for at bruge måneder på at kortlægge, designe og opfinde løsningen selv, går I direkte ind i et færdigt system med processer, automatisering og dokumentation, der virker fra dag ét.
                </p>
                <p className="public-section__copy">
                  Det forkorter implementeringstiden væsentligt, fordi system, specialister og løbende vedligeholdelse er inkluderet i Obsero Enterprise. På få måneder har I ikke afsluttet et projekt — I har compliance kørende i driften.
                </p>
              </div>

            </div>

            <div className="public-stack-list">
              {enterpriseHighlights.map((item, index) => (
                <article key={item.title} className="public-stack-item">
                  <div className="public-stack-item__index">{String(index + 1).padStart(2, "0")}</div>
                  <div className="public-stack-item__body">
                    <h3 className="public-stack-item__title">{item.title}</h3>
                    <p className="public-stack-item__copy">{item.copy}</p>
                  </div>
                </article>
              ))}
            </div>

          </div>
        </div>
      </section>

      <section className="public-section public-section--shell-band public-section--service-now-band">
        <div className="public-container">
          <div className="public-split-callout public-split-callout--middle">
            <div className="public-split-callout__lead">
              <SectionHeading title="ServiceNow sikrer compliance i fuld kontrol." />

              <div className="public-copy-stack">
                <p className="public-section__copy">
                  Compliance skal integreres i hverdagens systemer — ellers fejler det. Ingen bruger dem. Dokumentationen bliver forældet. Kontrollerne bliver glemt.
                </p>
                <p className="public-section__copy">
                  ServiceNow er forskellen mellem compliance på papiret og compliance i virkeligheden. Det er en enterprise-platform bygget til at automatisere komplekse processer, håndtere opgaveflow på tværs af organisationen og dokumentere alt i realtid — præcis det compliance kræver.
                </p>
              </div>
            </div>

            <img
              className="public-enterprise-illustration"
              src={enterpriseServiceNowIllustration}
              alt="ServiceNow og Obsero understøtter automatisering, opgavestyring, audit trail, risikobillede og dokumentation i realtid"
            />
          </div>
        </div>
      </section>

      <section className="public-section public-section--white-band public-section--module-support">
        <div className="public-container">
          <SectionHeading
            title="Fire områder. Én samlet ServiceNow-understøttet løsning."
            className="public-section__header--centered"
          />

          <div className="public-module-support">
            <div className="public-module-support__cards">
              {serviceNowModuleSupport.map((module) => (
                <article key={module.title} className="public-module-support-card">
                  <div className="public-module-support-card__badge">
                    <i className={module.icon} aria-hidden="true" />
                  </div>

                  <div className="public-module-support-card__inner">
                    <h3 className="public-module-support-card__title">{module.title}</h3>
                    <p className="public-module-support-card__copy">{module.copy}</p>
                    {module.serviceNow ? (
                      <div className="public-module-support-card__service">
                        <span>Understøttet af</span>
                        <img src={serviceNowLogo} alt="ServiceNow" />
                      </div>
                    ) : null}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="public-section public-section--white-band" id="moduler">
        <div className="public-container">
          <SectionHeading
            title="Governance, hændelser, risiko og leverandører i én samlet model."
            className="public-section__header--centered"
          />

          <ServiceCoverageMatrix
            serviceLabel="Obsero Service"
            sideLabel="Compliance as a Service"
            columns={enterpriseServiceColumns}
            rows={enterpriseServiceRows}
          />
        </div>
      </section>

      <section className="public-section public-section--shell-band">
        <div className="public-container">
          <SectionHeading
            title="Fra kontrakt til compliance i drift på få måneder."
          />

          <div className="public-journey-grid public-journey-grid--phases public-journey-grid--compact">
            {deliveryPhases.map((phase) => (
              <article key={phase.phase} className="public-journey-card public-journey-card--phase">
                <div className="public-journey-card__badge">
                  <i className={phase.icon} aria-hidden="true" />
                </div>
                <div className="public-journey-card__inner">
                  <h3 className="public-phase-card__title">{phase.phase}</h3>
                  <p className="public-phase-card__copy">{phase.copy}</p>
                  <ul className="public-phase-card__list">
                    {phase.deliverables.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="public-section public-section--white-band">
        <div className="public-container">
          <div className="public-split-callout public-split-callout--balanced">
            <div className="public-split-callout__lead">
              <SectionHeading
                title="Prioritér compliance nu — og få styr på ansvaret."
              />

              <div className="public-copy-stack">
                <p className="public-section__copy">
                  Obsero Enterprise er for organisationer, der hurtigt vil gå fra uafklaret ansvar og spredt dokumentation til et samlet compliance-setup i drift.
                </p>
                <p className="public-section__copy">
                  Når I køber adgang, får I et gennemtestet fundament, specialister ved siden af jer og en model, der holder compliance levende over tid.
                </p>
              </div>

              <ul className="public-module-list">
                {enterprisePriorityReasons.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>

            <article className="public-enterprise-package">
              <h3 className="public-enterprise-package__title">Et partnerskab med resultatgaranti.</h3>

              <div className="public-enterprise-package__items">
                {partnershipCards.map((card) => (
                  <div key={card.title} className="public-enterprise-package__item">
                    <h4>{card.title}</h4>
                    <p>{card.copy}</p>
                  </div>
                ))}
              </div>

            </article>
          </div>
        </div>
      </section>

      <CtaBand
        eyebrow="Se løsningen i praksis"
        title="Ved første møde demonstrerer vi den fulde løsning i ServiceNow."
        copy="I ser præcis, hvad I køber, inden I skriver under. Ingen forpligtelse, bare et klart billede af, hvordan Obsero Enterprise kan bringe compliance i drift hos jer."
        actions={[
          { href: siteLinks.contact, label: "Kontakt os", className: "btn btn-light" }
        ]}
      />
    </>
  );
}

Enterprise.layout = (page) => <PublicLayout>{page}</PublicLayout>;

export default Enterprise;
